import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import { showToast } from '@klpkonsern/ui';
import {
    ActionLink,
    Block,
    Heading,
    Loader,
    IconName,
    Illustration,
} from '@klp/design';
import {
    ContractManagementRoutes,
    ContractManagementTabs,
    ContractRegistrationRoutes,
} from '@forvalter/routing/Routes';
import {
    useGetContractRegistrationProcessQuery,
    useGetContractToRegistrationQuery,
} from '../services/rtkApi';
import { useSignalR } from '../services/signalR';
import ChatView from '@klp/contractmanagement/components/ChatView';
import { Main } from '@klp/layout/Main';
import ContractRegistrationProcess from '../components/ContractRegistrationProcess';
import { ContractSummary } from '../components/ContractSummary';
import {
    contractToRegistrationProcessSelectors,
    updateProcess,
    updateActivity,
} from '../contractToRegistrationSlice';
import type {
    ContractToRegistrationProcessResponse,
    IContractToRegistrationProcessActivity,
} from '../classes/ContractToRegistrationProcessResponse';

const useStyles = makeStyles(() => ({
    chatWrapper: {
        width: '100%',
        position: 'relative',
    },
    notFoundContainer: {
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '60%',
    },
}));

export default function ContractRegistrationView() {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const { kontraktId } = useParams<{ kontraktId: string }>();
    const [processLoading, setProcessLoading] = useState(false);
    const {
        startConnection,
        stopConnection,
        connected,
        connection,
        invoke,
        on,
        off,
    } = useSignalR();
    const registrationProcessDataSignalR = useSelector(
        contractToRegistrationProcessSelectors,
    );

    const {
        data: contractToRegistration,
        isLoading: contractToRegistrationLoading,
        isFetching: contractToRegistrationFetching,
        isError: contractToRegistrationError,
    } = useGetContractToRegistrationQuery({
        kontraktId,
    });

    const {
        data: registrationProcessData,
        isLoading: registrationProcessLoading,
        isFetching: registrationProcessFetching,
    } = useGetContractRegistrationProcessQuery({
        kontraktDokumentId: kontraktId,
        startNewProcess: false,
        culture: i18n.language,
        signalRConnected: connected,
    });

    useEffect(() => {
        on(
            'ProcessChangedFull',
            (receivedData: ContractToRegistrationProcessResponse) => {
                setProcessLoading(true);
                dispatch(updateProcess(receivedData));
                setProcessLoading(false);
            },
        );
        on(
            'ProcessChangedActivity',
            (receivedData: IContractToRegistrationProcessActivity) => {
                if (document.hasFocus()) {
                    return;
                }
                setProcessLoading(true);
                dispatch(updateActivity(receivedData));
                setProcessLoading(false);
            },
        );

        return () => {
            off('ProcessChangedFull');
            off('ProcessChangedActivity');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected, on, off]);

    const handleUnsubscribe = useCallback(async () => {
        if (!connected || !connection) return;
        try {
            await invoke('Unsubscribe', kontraktId);
            off('ProcessChangedTask');
            await stopConnection();
        } catch (error) {
            console.error('Error unsubscribing:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kontraktId, connected, connection]);

    const handleSubscribe = useCallback(async () => {
        if (!connected || !connection) {
            return startConnection();
        }

        try {
            await invoke('Subscribe', kontraktId);
        } catch (error) {
            console.error('Error subscribing:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kontraktId, connected, connection]);

    useEffect(() => {
        handleSubscribe();

        return () => {
            handleUnsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kontraktId, connected, connection]);

    const completedContractRegistration = useMemo(
        () =>
            !contractToRegistration ||
            contractToRegistration.registreringsProsesser.length === 1
                ? false
                : contractToRegistration.registreringsProsesser.find(
                      (c) => c.type === 'Kontrakt',
                  ),
        [contractToRegistration],
    );

    const isLoading =
        contractToRegistrationLoading || contractToRegistrationFetching;

    if (isLoading) return <Loader isLoading={isLoading} />;

    const process = connected
        ? registrationProcessDataSignalR
        : registrationProcessData;

    if (!contractToRegistration || contractToRegistrationError) {
        showToast({
            body: t('checkOffContract.error'),
            type: 'error',
        });
        return (
            <Main fullScreen={true}>
                <Main.Navigation>
                    <ActionLink
                        actionType="linkBack"
                        translation="checkOffContract.backButton"
                        href={ContractManagementRoutes.tab(
                            ContractManagementTabs.registrering,
                        )}
                    />
                </Main.Navigation>
                <Block
                    className={classes.notFoundContainer}
                    paddingTop="massive"
                >
                    <Illustration
                        paddingBottom="compact"
                        icon={IconName.CIRCLE_OBS}
                        size="xxxlarge"
                    />
                    <Heading
                        klpHeadingLevel="TWO"
                        klpColor="dark"
                        translation="checkOffContract.error"
                        data-cy="CheckOffContract__NotFound__Title"
                    />
                </Block>
            </Main>
        );
    }

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="contractManagement"
                    label={t('breadcrumbs.contractManagement')}
                    href={ContractManagementRoutes.moduleRoute()}
                />
                <Main.Crumb
                    id="checkOffContract"
                    label={contractToRegistration.navn}
                />
            </Main.Breadcrumbs>
            <Main.Navigation>
                <ActionLink
                    actionType="linkBack"
                    translation="checkOffContract.backButton"
                    href={ContractManagementRoutes.tab(
                        ContractManagementTabs.registrering,
                    )}
                />
            </Main.Navigation>
            <div className={classes.chatWrapper}>
                <ChatView />
            </div>

            <ContractSummary contract={contractToRegistration} />
            {completedContractRegistration && (
                <Block>
                    <ActionLink
                        actionType="link"
                        translation="checkOffContract.registreringProsessLink"
                        translationOptions={{
                            type: t(
                                `contractRegistration.contractTypes.${completedContractRegistration.type}`,
                            ),
                        }}
                        href={ContractRegistrationRoutes.singleInstance(
                            kontraktId,
                            completedContractRegistration.id,
                        )}
                    />
                </Block>
            )}
            <Loader
                isLoading={
                    registrationProcessLoading ||
                    registrationProcessFetching ||
                    processLoading
                }
            >
                {process && (
                    <ContractRegistrationProcess
                        process={process}
                        skalRegistreres={contractToRegistration.skalRegistreres}
                    />
                )}
            </Loader>
        </Main>
    );
}
